import React, { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import Papa from 'papaparse'
import { supabase } from '../../lib/supabase'
import Toast from '../ui/Toast'

const { FiUpload, FiDownload, FiFile, FiCheck, FiAlertCircle, FiX } = FiIcons

const CSVUploader = () => {
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState([])
  const [errors, setErrors] = useState([])
  const [success, setSuccess] = useState(false)
  const [toast, setToast] = useState(null)
  const fileInputRef = useRef(null)

  const csvTemplate = `fecha,nombre,categoria,descripcion,alergenos,precio,visible,orden
2024-01-22,Sopa de Verduras,primero,"Sopa casera con verduras frescas","",NULL,true,1
2024-01-22,Pollo a la Plancha,segundo,"Pechuga de pollo a la plancha con guarnición","",NULL,true,1
2024-01-27,Silpancho,tipico,"Escalope empanizado con arroz y papa","gluten,huevos",12.50,true,1
2024-01-27,Parrillada Mixta,parrilla,"Chorizo, morcilla, chuleta y carne","",18.00,true,1
2024-01-22,Estrella Galicia,cerveza,"Cerveza rubia 33cl","",2.50,true,1`

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0]
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile)
      setErrors([])
      setSuccess(false)
      parseCSV(selectedFile)
    } else {
      setErrors(['Por favor, selecciona un archivo CSV válido.'])
      setFile(null)
      setPreview([])
    }
  }

  const parseCSV = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const { data, errors: parseErrors } = results

        if (parseErrors.length > 0) {
          setErrors(parseErrors.map(error => `Línea ${error.row}: ${error.message}`))
          return
        }

        // Validate data
        const validationErrors = []
        const validatedData = []

        data.forEach((row, index) => {
          const lineNumber = index + 2 // +2 because index starts at 0 and we have header

          // Required fields validation
          if (!row.fecha) validationErrors.push(`Línea ${lineNumber}: Fecha es requerida`)
          if (!row.nombre) validationErrors.push(`Línea ${lineNumber}: Nombre es requerido`)
          if (!row.categoria) validationErrors.push(`Línea ${lineNumber}: Categoría es requerida`)

          // Category validation
          const validCategories = ['primero', 'segundo', 'tipico', 'parrilla', 'bebida_casa', 'bebida_botella', 'cerveza']
          if (row.categoria && !validCategories.includes(row.categoria)) {
            validationErrors.push(`Línea ${lineNumber}: Categoría debe ser una de: ${validCategories.join(', ')}`)
          }

          // Price validation based on category
          const requiresPriceCategories = ['tipico', 'parrilla', 'bebida_casa', 'bebida_botella', 'cerveza']
          const menuCategories = ['primero', 'segundo']

          if (requiresPriceCategories.includes(row.categoria)) {
            if (!row.precio || row.precio === 'NULL' || isNaN(parseFloat(row.precio))) {
              validationErrors.push(`Línea ${lineNumber}: Precio es requerido para la categoría ${row.categoria}`)
            }
          }

          if (menuCategories.includes(row.categoria)) {
            if (row.precio && row.precio !== 'NULL' && !isNaN(parseFloat(row.precio))) {
              validationErrors.push(`Línea ${lineNumber}: Los platos de menú (${row.categoria}) no deben tener precio`)
            }
          }

          // Process allergens
          const alergenos = row.alergenos ? row.alergenos.split(',').map(a => a.trim()).filter(a => a) : []

          // Process visible field - FIX: Properly parse boolean values
          let visible = true // Default to true
          if (row.visible !== undefined && row.visible !== null && row.visible !== '') {
            const visibleStr = row.visible.toString().toLowerCase()
            visible = visibleStr === 'true' || visibleStr === '1' || visibleStr === 'yes' || visibleStr === 'si'
          }

          if (validationErrors.length === 0 || validationErrors.filter(e => e.includes(`Línea ${lineNumber}`)).length === 0) {
            validatedData.push({
              ...row,
              alergenos,
              precio: (row.precio && row.precio !== 'NULL') ? parseFloat(row.precio) : null,
              visible, // Use the properly parsed boolean value
              orden: parseInt(row.orden) || 0
            })
          }
        })

        if (validationErrors.length > 0) {
          setErrors(validationErrors)
          setPreview([])
        } else {
          setErrors([])
          setPreview(validatedData)
          console.log('Parsed data:', validatedData) // Debug log
        }
      },
      error: (error) => {
        setErrors([`Error al procesar el archivo: ${error.message}`])
      }
    })
  }

  const handleUpload = async () => {
    if (preview.length === 0) return

    setLoading(true)
    try {
      console.log('Uploading data:', preview) // Debug log
      
      const { error } = await supabase
        .from('platos')
        .insert(preview)

      if (error) {
        console.error('Error uploading data:', error)
        showToast('Error al subir los datos: ' + error.message, 'error')
        return
      }

      setSuccess(true)
      setFile(null)
      setPreview([])
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      
      showToast(`¡Éxito! Se han importado ${preview.length} platos.`)
      setTimeout(() => setSuccess(false), 5000)
    } catch (error) {
      console.error('Error uploading data:', error)
      showToast('Error al subir los datos. Por favor, inténtalo de nuevo.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const downloadTemplate = () => {
    const blob = new Blob([csvTemplate], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'plantilla_menu_vipbar.csv'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  const clearFile = () => {
    setFile(null)
    setPreview([])
    setErrors([])
    setSuccess(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-vip-orange">Carga Masiva CSV</h1>
        <p className="text-vip-brown">Importa múltiples platos desde un archivo CSV</p>
      </div>

      {/* Template Download */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Plantilla CSV
            </h3>
            <p className="text-blue-800">
              Descarga la plantilla actualizada para el nuevo sistema de menús
            </p>
          </div>
          <button
            onClick={downloadTemplate}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            <SafeIcon icon={FiDownload} className="mr-2" />
            Descargar Plantilla
          </button>
        </div>
      </motion.div>

      {/* File Upload */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-md p-6"
      >
        <h3 className="text-lg font-semibold text-vip-brown mb-4">
          Subir Archivo CSV
        </h3>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-vip-orange transition-colors duration-200">
          <SafeIcon icon={FiFile} className="text-4xl text-gray-400 mx-auto mb-4" />
          {!file ? (
            <>
              <p className="text-gray-600 mb-4">
                Arrastra un archivo CSV aquí o haz clic para seleccionar
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center justify-center mx-auto px-4 py-2 bg-vip-orange text-white rounded-md hover:bg-vip-dark transition-colors duration-200"
              >
                <SafeIcon icon={FiUpload} className="mr-2" />
                Seleccionar Archivo
              </button>
            </>
          ) : (
            <div className="flex items-center justify-center space-x-4">
              <SafeIcon icon={FiFile} className="text-vip-orange text-2xl" />
              <span className="text-gray-700 font-medium">{file.name}</span>
              <button
                onClick={clearFile}
                className="text-red-500 hover:text-red-700 transition-colors duration-200"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Errors */}
      {errors.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <div className="flex items-center mb-3">
            <SafeIcon icon={FiAlertCircle} className="text-red-600 mr-2" />
            <h4 className="font-semibold text-red-900">Errores encontrados:</h4>
          </div>
          <ul className="space-y-1 text-red-800 text-sm">
            {errors.map((error, index) => (
              <li key={index} className="flex items-start">
                <span className="inline-block w-1 h-1 bg-red-600 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                {error}
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      {/* Success Message */}
      {success && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-200 rounded-lg p-4"
        >
          <div className="flex items-center">
            <SafeIcon icon={FiCheck} className="text-green-600 mr-2" />
            <span className="text-green-800 font-medium">
              ¡Archivo subido exitosamente! Se han importado {preview.length} platos.
            </span>
          </div>
        </motion.div>
      )}

      {/* Preview */}
      {preview.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-md overflow-hidden"
        >
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-vip-brown">
              Vista Previa ({preview.length} platos)
            </h3>
            <button
              onClick={handleUpload}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-vip-orange text-white rounded-md hover:bg-vip-dark transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Subiendo...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiUpload} className="mr-2" />
                  Confirmar Subida
                </>
              )}
            </button>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Visible</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {preview.slice(0, 10).map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-900">{row.fecha}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{row.nombre}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {row.categoria}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900">
                      {row.precio ? `${row.precio.toFixed(2)}€` : 'Menú'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        row.visible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {row.visible ? 'Sí' : 'No'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.length > 10 && (
              <div className="p-4 text-center text-gray-500 text-sm">
                ... y {preview.length - 10} platos más
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Instructions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-gray-50 border border-gray-200 rounded-lg p-6"
      >
        <h4 className="font-semibold text-gray-900 mb-3">Nuevo formato del archivo CSV:</h4>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div>
            <h5 className="font-medium mb-2">Campos requeridos:</h5>
            <ul className="space-y-1">
              <li>• <strong>fecha:</strong> YYYY-MM-DD</li>
              <li>• <strong>nombre:</strong> Nombre del plato</li>
              <li>• <strong>categoria:</strong> primero, segundo, tipico, parrilla, bebida_casa, bebida_botella, cerveza</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium mb-2">Reglas de precios:</h5>
            <ul className="space-y-1">
              <li>• <strong>Primeros/Segundos:</strong> precio = NULL (menú 13€)</li>
              <li>• <strong>Otros:</strong> precio requerido</li>
              <li>• <strong>visible:</strong> true/false (por defecto: true)</li>
              <li>• <strong>orden:</strong> número para ordenar</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default CSVUploader